import {
  KillChainCategory,
  type StudyAdversaryProfile,
  type StudyListSelectDto,
  StudyListSelectDtoStatusEnum,
  type StudyTimeHorizon,
} from '@@types/api.types';

export type MeiaStudiesFilters = {
  adversaryProfiles: StudyAdversaryProfile[];
  pattern: KillChainCategory | 'all';
  search: string;
  status: StudyListSelectDtoStatusEnum | 'all';
  timeHorizons: StudyTimeHorizon[];
};

export const DEFAULT_MEIA_STUDIES_FILTERS: MeiaStudiesFilters = {
  adversaryProfiles: [],
  pattern: 'all',
  search: '',
  status: 'all',
  timeHorizons: [],
};

/** All selectable study statuses for the status filter, in workflow order (Archived last). */
export const STUDY_STATUS_OPTIONS: StudyListSelectDtoStatusEnum[] = [
  StudyListSelectDtoStatusEnum.DRAFT,
  StudyListSelectDtoStatusEnum.PROPOSED,
  StudyListSelectDtoStatusEnum.APPROVED,
  StudyListSelectDtoStatusEnum.ARCHIVED,
];

/** Kill-chain pattern options for the pattern filter. */
export const STUDY_PATTERN_OPTIONS: KillChainCategory[] = Object.values(KillChainCategory);

function matchesSearch(study: StudyListSelectDto, search: string): boolean {
  const normalized = search.trim().toLowerCase();
  if (normalized.length === 0) return true;
  return study.name.toLowerCase().includes(normalized);
}

function matchesStatus(study: StudyListSelectDto, status: MeiaStudiesFilters['status']): boolean {
  if (status === 'all') return true;
  return study.status === status;
}

function matchesAdversaryProfiles(study: StudyListSelectDto, adversaryProfiles: StudyAdversaryProfile[]): boolean {
  if (adversaryProfiles.length === 0) return true;
  return adversaryProfiles.some((profile) => study.adversaryProfiles.includes(profile));
}

function matchesTimeHorizons(study: StudyListSelectDto, timeHorizons: StudyTimeHorizon[]): boolean {
  if (timeHorizons.length === 0) return true;
  if (!study.timeHorizon) return false;
  return timeHorizons.includes(study.timeHorizon);
}

function matchesPattern(study: StudyListSelectDto, pattern: MeiaStudiesFilters['pattern']): boolean {
  if (pattern === 'all') return true;
  return study.threadKillChains.includes(pattern);
}

/**
 * Pure filter pipeline over the already-fetched study list. The status axis owns
 * Active/Archived selection now that the scope tabs are gone — `all` shows every
 * status (including ARCHIVED), or a single status narrows the list.
 */
export function filterMeiaStudies(studies: StudyListSelectDto[], filters: MeiaStudiesFilters): StudyListSelectDto[] {
  return studies.filter(
    (study) =>
      matchesSearch(study, filters.search) &&
      matchesStatus(study, filters.status) &&
      matchesPattern(study, filters.pattern) &&
      matchesAdversaryProfiles(study, filters.adversaryProfiles) &&
      matchesTimeHorizons(study, filters.timeHorizons),
  );
}

export function toggleArrayMember<T>(values: T[], member: T): T[] {
  return values.includes(member) ? values.filter((value) => value !== member) : [...values, member];
}
